/**
 * A short label that sets one thing apart: a count, a role, a state. It always carries a word, so
 * the tone is never the only way to tell it from the others. It is not a live region. A line that
 * announces progress is `Status`.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import { textStyle } from "./textStyle.ts";
const styles = {
  badge: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xskm3c9",
    ks0D6T: "x193iq5w",
    k8WAf4: "xyjizu4",
    kg3NbH: "xc7sml1",
    kaIpWk: "x1m3fsnl",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  neutral: {
    kWkggS: "x14ba300",
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  ok: {
    kWkggS: "xl5rrbb",
    kMwMTN: "xdhxsyf",
    $$css: true
  },
  warn: {
    kWkggS: "x1n3p3y5",
    kMwMTN: "x1j243yh",
    $$css: true
  },
  danger: {
    kWkggS: "xqsqr6",
    kMwMTN: "x6ygkrl",
    $$css: true
  },
  info: {
    kWkggS: "x3oldu3",
    kMwMTN: "x10dbofl",
    $$css: true
  },
  dot: {
    kzqmXN: "x7rs3l3",
    kZKoxP: "xsf620a",
    kaIpWk: "x1m3fsnl",
    kmuXW: "x2lah0s",
    $$css: true
  },
  dotNeutral: {
    kWkggS: "x1up07ya",
    $$css: true
  },
  dotOk: {
    kWkggS: "xilz7qp",
    $$css: true
  },
  dotWarn: {
    kWkggS: "x1glfd2z",
    $$css: true
  },
  dotDanger: {
    kWkggS: "xz15occ",
    $$css: true
  },
  dotInfo: {
    kWkggS: "xmbr0q7",
    $$css: true
  }
};
export type BadgeTone = "neutral" | "ok" | "warn" | "danger" | "info";
const TONE = {
  neutral: styles.neutral,
  ok: styles.ok,
  warn: styles.warn,
  danger: styles.danger,
  info: styles.info
} as const;
const DOT = {
  neutral: styles.dotNeutral,
  ok: styles.dotOk,
  warn: styles.dotWarn,
  danger: styles.dotDanger,
  info: styles.dotInfo
} as const;
export function Badge({
  tone = "neutral",
  dot = false,
  children,
  testId
}: {
  readonly tone?: BadgeTone;
  /** A dot before the word, so a list of states can be scanned by shape as well as colour. */
  readonly dot?: boolean;
  readonly children: ReactNode;
  readonly testId?: string;
}) {
  return <span data-testid={testId} {...stylex.props(textStyle.label, styles.badge, TONE[tone])}>
      {dot && <span aria-hidden="true" {...stylex.props(styles.dot, DOT[tone])} />}
      {children}
    </span>;
}